// Egg production (BIOLOGY-ENGINE.md §3.6). The honest rhythm: a hen ovulates
// every ~25.5h, so her laying time drifts later each day until it would pass
// mid-afternoon — then she skips a day and resets to the morning.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type { ChickenEntity, StoredEgg, WeatherState } from '../core/types';
import { breedProfile, type BiologyConfig } from './config';
import { hiddenStatMultiplier } from './access';
import { eggShellColor } from './genetics';
import { isLayingAge, isMolting } from './lifecycle';
import { canLay } from './physiology';
import { dayOf, hourOfDay } from './time';

/** Hours of daylight from the local sun times. */
export function daylightHours(weather: WeatherState): number {
  return clamp(weather.sunsetHour - weather.sunriseHour, 0, 24);
}

/**
 * Sustained laying needs ~14h of light. PR's ~11h winter days cause a real dip
 * — fixable with the timed coop light, with an honest welfare fact card.
 */
export function daylightFactor(
  weather: WeatherState,
  config: BiologyConfig,
  hasCoopLight: boolean,
): number {
  if (hasCoopLight) return 1;
  const hours = daylightHours(weather);
  const full = config.eggs.minDaylightHours;
  if (hours >= full) return 1;

  // Graded, not a cliff. The old version snapped straight to the penalty below
  // 14 hours, which is the temperate-winter rule of thumb — and Puerto Rico sits
  // at 18.4°N, where daylight tops out near 13.1 hours at the solstice. Every
  // hen in the game's home setting was therefore stuck at 55% production for
  // life, with the only remedy being a coop light. Real hens respond to
  // photoperiod along a curve, and tropical flocks lay perfectly well on a
  // steady 12–13 hours.
  const floor = SHORT_DAY_FLOOR_HOURS;
  if (hours <= floor) return config.eggs.shortDayPenalty;
  const t = (hours - floor) / (full - floor);
  return config.eggs.shortDayPenalty + (1 - config.eggs.shortDayPenalty) * t;
}

/** Below this a short day bites at full strength — a genuine northern winter. */
const SHORT_DAY_FLOOR_HOURS = 10;

/** Production peaks in year one, then falls 10–15% a year. */
export function ageCurve(chicken: ChickenEntity, config: BiologyConfig): number {
  const weeks = chicken.ageInDays / 7;
  const onset = breedProfile(chicken.breed).onsetWeeks;
  if (weeks < onset) return 0;
  // Point-of-lay hens ramp up over their first month of laying.
  if (weeks < onset + 4) return 0.5 + 0.5 * ((weeks - onset) / 4);
  if (weeks <= config.eggs.peakWeeks) return 1;
  const yearsPastPeak = (weeks - config.eggs.peakWeeks) / 52;
  return clamp(1 - config.eggs.annualDeclinePerYear * yearsPastPeak, 0.3, 1);
}

export interface LayContext {
  weather: WeatherState;
  hasCalcium: boolean;
  hasCoopLight: boolean;
  hasNestBox: boolean;
  currentDay: number;
}

/**
 * P(egg today) per BIOLOGY-ENGINE.md §3.6:
 * breedRate/7 × bodyCondition% × (1 − stress/150) × calcium × daylight × age.
 */
export function dailyLayProbability(
  chicken: ChickenEntity,
  context: LayContext,
  config: BiologyConfig,
): number {
  if (!isLayingAge(chicken)) return 0;
  if (isMolting(chicken, context.currentDay)) return 0;
  if (!canLay(chicken.physiology)) return 0;

  const breed = breedProfile(chicken.breed);
  const p = chicken.physiology;
  let probability =
    (breed.eggRatePerWeek / 7) *
    (p.bodyCondition / 100) *
    (1 - p.stress / 150) *
    (context.hasCalcium ? 1 : config.eggs.calciumPenalty) *
    daylightFactor(context.weather, config, context.hasCoopLight) *
    ageCurve(chicken, config) *
    // This bird's own hidden number, ±10% on the breed average (PLAN-04 S4).
    // The player learns it by watching her, which is the point of it being
    // hidden; the sim always uses the real value.
    hiddenStatMultiplier(chicken, 'eggs');

  probability *= heatProductionFactor(context.weather.tempF, config);
  return clamp(probability, 0, 1);
}

/**
 * How much heat is suppressing laying, 0–1, where 1 is no suppression.
 *
 * Heat suppresses laying before it does anything else visible. It used to be a
 * single boolean at `comfortHighF` (80°F) applying a flat cut, which is wrong
 * in two directions at once: 81°F cost as much as 105°F, and in Puerto Rico —
 * where the default weather sits at 84°F and 85–89°F is an ordinary day — the
 * full penalty simply applied every day forever, teaching nothing about heat.
 *
 * Now it ramps from no penalty at the comfort ceiling to the full penalty at
 * the severe-heat threshold, and keeps worsening beyond it up to double. That
 * matches how production actually falls off, and it makes shade and cool water
 * read as the mitigations they are rather than as decoration.
 */
export function heatProductionFactor(tempF: number, config: BiologyConfig): number {
  const comfort = config.temperature.comfortHighF;
  if (tempF <= comfort) return 1;

  const severe = config.temperature.severeHeatF;
  const full = config.eggs.heatProductionPenalty;
  const t = severe > comfort ? (tempF - comfort) / (severe - comfort) : 1;
  // Past severe heat the penalty keeps growing, to twice the configured cut.
  const penalty = full * clamp(t, 0, 2);
  return clamp(1 - penalty, 0, 1);
}

/** True when the ovulation clock says this hen is due to lay right now. */
export function isDueToLay(chicken: ChickenEntity, simHour: number): boolean {
  return simHour >= chicken.eggs.nextLayAtHour;
}

/**
 * Starts the ovulation clock the first time a hen reaches point of lay.
 *
 * A bird hatches with `nextLayAtHour: Infinity`, which is correct — a chick has
 * no clock to run. But the only code that ever replaced that Infinity was
 * `advanceOvulation`, and it sat *behind* the `isDueToLay` guard, which can
 * never pass while the value is Infinity. So the clock started only for a hen
 * whose clock had already started: no hen in the game could ever lay an egg.
 *
 * Point of lay is its own event in a real bird's life and belongs here, ahead
 * of the laying check rather than inside it.
 */
export function beginLaying(chicken: ChickenEntity, simHour: number): boolean {
  if (chicken.gender !== 'hen' || !isLayingAge(chicken)) return false;
  if (Number.isFinite(chicken.eggs.nextLayAtHour)) return false;
  // Her first egg comes the following morning at her own offset, not the
  // instant she crosses the age line.
  chicken.eggs.nextLayAtHour = (dayOf(simHour) + 1) * 24 + 6 + chicken.eggs.ovulationOffsetHours;
  return true;
}

/**
 * Advances the ovulation clock. Past mid-afternoon she skips the day and resets
 * to the morning — the drift-and-reset pattern real keepers notice.
 */
export function advanceOvulation(
  chicken: ChickenEntity,
  simHour: number,
  config: BiologyConfig,
): void {
  const next = simHour + config.eggs.ovulationHours;
  const nextHourOfDay = hourOfDay(next);
  if (nextHourOfDay > 15) {
    // Skip a day; resume tomorrow morning at her personal offset.
    const morning = (dayOf(next) + 1) * 24 + 6 + chicken.eggs.ovulationOffsetHours;
    chicken.eggs.nextLayAtHour = morning;
  } else {
    chicken.eggs.nextLayAtHour = next;
  }
}

const SIZE_BY_AGE: Array<{ maxWeeks: number; size: StoredEgg['size'] }> = [
  { maxWeeks: 26, size: 'S' },
  { maxWeeks: 40, size: 'M' },
  { maxWeeks: 80, size: 'L' },
  { maxWeeks: Infinity, size: 'XL' },
];

export function eggSizeFor(chicken: ChickenEntity, rng: Rng): StoredEgg['size'] {
  const weeks = chicken.ageInDays / 7;
  const base = SIZE_BY_AGE.find((s) => weeks <= s.maxWeeks)!.size;
  // Silkie eggs are small whatever her age.
  if (chicken.breed === 'silkie') return rng.chance(0.7) ? 'S' : 'M';
  return base;
}

export function createEgg(chicken: ChickenEntity, simHour: number, rng: Rng): StoredEgg {
  return {
    id: `egg_${rng.int(0, 0xffffff).toString(16).padStart(6, '0')}`,
    shell: eggShellColor(chicken.genotype),
    layerId: chicken.id,
    laidAtHour: simHour,
    size: eggSizeFor(chicken, rng),
    genotype: { ...chicken.genotype },
  };
}

/**
 * Eggs left sitting teach a hen to eat them — a real, persistent bad habit and
 * the reason daily collection matters beyond the noise (EDUCATION-DESIGN.md §5).
 */
export function shouldLearnEggEating(
  eggs: StoredEgg[],
  simHour: number,
  config: BiologyConfig,
): boolean {
  const stale = config.eggs.eggEatingHabitDays * 24;
  return eggs.some((egg) => simHour - egg.laidAtHour >= stale);
}

export interface SoonLay {
  name: string;
  nextLayAtHour: number;
}

/**
 * Finds the hen whose next lay is imminent, for the HUD nudge. Returns null when
 * no living hen has a finite clock or nothing is due within the window. A hen is
 * "almost ready" in the two hours before her ovulation clock fires, not after.
 */
export function findSoonestLay(state: { flock: ChickenEntity[]; simHour: number }): SoonLay | null {
  let soonest: SoonLay | null = null;
  let soonestDelta = 48;
  for (const chicken of state.flock) {
    if (!chicken.alive || chicken.gender !== 'hen') continue;
    const next = chicken.eggs.nextLayAtHour;
    if (!Number.isFinite(next)) continue;
    const delta = next - state.simHour;
    if (delta <= 0 || delta > soonestDelta) continue;
    soonestDelta = delta;
    soonest = { name: chicken.name, nextLayAtHour: next };
  }
  return soonest;
}
